import { Link } from '@tanstack/react-router'
import { ReactNode, useState } from 'react'

import { Order } from '@/api/admin-orders-types'
import { Button } from '@/components/base/button'
import { Container } from '@/components/base/container'
import { Heading } from '@/components/base/heading'
import { Text } from '@/components/base/text'
import { Thumbnail } from '@/components/base/thumbnail'
import { useI18n } from '@/i18n/i18n-react'
import { TriangleDownMini } from '@/icons/triangle-down-mini'
import { clx } from '@/utils/clx'
import { fromPrecision, largerThan, multiply, smallerThan, sum } from '@/utils/currency'

const PointsBadge = ({ children }: { children: ReactNode }) => (
	<span className='inline-flex items-center rounded-md bg-ft-surface-mint px-1.5 py-0.5 text-xs font-medium tabular-nums text-ft-brand-obsidian'>
		{children}
	</span>
)

export const OrderSummarySection = ({ order }: { order: Order }) => {
	return (
		<Container className='divide-y divide-dashed'>
			<Header order={order} />
			<ItemBreakdown order={order} />
			<CostBreakdown order={order} />
			<Total order={order} />
		</Container>
	)
}

const Header = ({ order }: { order: Order }) => {
	const { L } = useI18n()

	return (
		<div className='flex items-center justify-between px-3 py-2 sm:px-6 sm:py-4'>
			<Heading level='h2'>{L.OrderDetail.Summary.Title()}</Heading>
			<div className='flex items-center gap-x-2'>
				<a href={`/orders/${order.uuid}/receipt`} target='_blank' rel='noopener noreferrer'>
					<Button className='shrink-0' size='small' variant='secondary'>
						<span className='hidden sm:block'>{L.OrderDetail.Summary.OpenReceipt()}</span>
					</Button>
				</a>
			</div>
		</div>
	)
}

const ItemBreakdown = ({ order }: { order: Order }) => {
	const { L } = useI18n()
	return (
		<div>
			{order.itemGroups.map(itemGroup => {
				const { name, items, shouldOverridePrice, totalPrice } = itemGroup
				return (
					<div key={itemGroup.uuid}>
						{shouldOverridePrice && (
							<div>
								<div
									className={clx(
										'grid grid-cols-1 items-center gap-x-4 px-3 py-2 text-ui-fg-subtle sm:grid-cols-2 sm:px-6 sm:py-4'
									)}
								>
									<div className='flex items-center gap-x-4'>
										<Thumbnail />
										<div>
											<Text
												size='small'
												leading='compact'
												weight='plus'
												className='text-ui-fg-base'
											>
												{name}
											</Text>
										</div>
									</div>

									<div className='grid grid-cols-3 items-center gap-x-4'>
										<div className='flex items-center justify-end gap-x-4'>
											<Text size='small'>
												{L.Amount.Default({
													amount: totalPrice,
													currencyCode: order.currencyCode
												})}
											</Text>
										</div>

										<div className='flex items-center gap-x-2'>
											<div className='w-fit min-w-[27px]'>
												<Text size='small'>
													<span className='tabular-nums'>{items.length}</span>
												</Text>
											</div>
										</div>

										<div className='flex items-center justify-end'>
											<Text size='small' className='pt-[1px]'>
												{L.Amount.Default({
													amount: totalPrice,
													currencyCode: order.currencyCode
												})}
											</Text>
										</div>
									</div>
								</div>
							</div>
						)}
						{items.map(item => {
							return (
								<div key={item.uuid} className={clx({ 'pl-8 line-through': shouldOverridePrice })}>
									<div
										key={item.uuid}
										className={clx(
											'grid grid-cols-1 items-center gap-x-4 px-3 py-2 text-ui-fg-subtle sm:grid-cols-2 sm:px-6 sm:py-4',
											{
												'pb-0': item.fees.length > 0 || (item.discounts?.length ?? 0) > 0
											}
										)}
									>
										<div className='flex items-center gap-x-4'>
											<Thumbnail src={item.variantThumbnail} />
											<div>
												{item.productUuid ? (
													<Link
														to='/products/$productUuid'
														params={{ productUuid: item.productUuid }}
													>
														<Text
															size='small'
															leading='compact'
															weight='plus'
															className='text-ui-fg-base hover:text-ui-fg-interactive hover:underline'
														>
															{item.variantName}
														</Text>
													</Link>
												) : (
													<Text
														size='small'
														leading='compact'
														weight='plus'
														className='text-ui-fg-base'
													>
														{item.variantName}
													</Text>
												)}
											</div>
										</div>

										<div className='grid grid-cols-3 items-center gap-x-4'>
											<div className='flex items-center justify-end gap-x-4'>
												<Text size='small'>
													{L.Amount.Default({
														amount: item.unitPrice,
														currencyCode: order.currencyCode,
														unitToDivideWith:
															item.quantityInfo.pricingMethod === 'PER_WEIGHT' ||
															item.quantityInfo.pricingMethod === 'WEIGHT_EMBEDDED'
																? 'kg'
																: undefined
													})}
												</Text>
											</div>

											<div className='flex items-center gap-x-2'>
												<div className='w-fit min-w-[27px]'>
													<Text size='small'>
														<span className='tabular-nums'>
															{item.quantityInfo.pricingMethod === 'PER_WEIGHT' ||
															item.quantityInfo.pricingMethod === 'WEIGHT_EMBEDDED'
																? fromPrecision(item.quantityInfo.weight) + 'kg'
																: L.Quantity.Pieces(item.quantityInfo.quantity)}
														</span>
													</Text>
												</div>
											</div>

											<div className='flex items-center justify-end gap-x-2'>
												{item.pointsEarned !== undefined && item.pointsEarned > 0 && (
													<PointsBadge>+{item.pointsEarned}</PointsBadge>
												)}
												<Text size='small' className='pt-[1px]'>
													{L.Amount.Default({
														amount: item.itemTotal,
														currencyCode: order.currencyCode
													})}
												</Text>
											</div>
										</div>
									</div>
									{(item.discounts ?? []).map(discount => (
										<div
											key={discount.uuid}
											className='grid grid-cols-1 items-center gap-x-4 px-3 pb-2 pt-1 text-ui-fg-subtle sm:grid-cols-2 sm:px-6 sm:pb-4 sm:pt-2'
										>
											<div className='ml-8 flex items-center gap-x-4'>
												<Text size='small' leading='compact' className='pl-4 text-ui-fg-base'>
													{discount.name || L.OrderDetail.Summary.Discount()}
												</Text>
											</div>
											<div className='grid grid-cols-3 items-center gap-x-4'>
												<div />
												<div />
												<div className='flex items-center justify-end gap-x-4'>
													<Text size='small'>
														{L.Amount.Default({
															amount: multiply(discount.amount, -1),
															currencyCode: order.currencyCode
														})}
													</Text>
												</div>
											</div>
										</div>
									))}
									{item.fees.map(fee => (
										<div
											key={fee.uuid}
											className='grid grid-cols-1 items-center gap-x-4 px-3 pb-2 pt-1 text-ui-fg-subtle sm:grid-cols-2 sm:px-6 sm:pb-4 sm:pt-2'
										>
											<div className='ml-8 flex items-center gap-x-4'>
												<Text size='small' leading='compact' className='pl-4 text-ui-fg-base'>
													{fee.name}
												</Text>
											</div>
											<div className='grid grid-cols-3 items-center gap-x-4'>
												<div className='flex items-center justify-end gap-x-4'>
													<Text size='small'>
														{L.Amount.Default({
															amount: fee.unitPrice,
															currencyCode: order.currencyCode
														})}
													</Text>
												</div>
												<div className='flex items-center gap-x-2'>
													<div className='w-fit min-w-[27px]'>
														<Text size='small'>
															<span className='tabular-nums'>
																{L.Quantity.Pieces(fee.quantity)}
															</span>
														</Text>
													</div>
												</div>

												<div className='flex items-center justify-end gap-x-4'>
													<Text size='small'>
														{L.Amount.Default({
															amount: fee.totalPrice,
															currencyCode: order.currencyCode
														})}
													</Text>
												</div>
											</div>
										</div>
									))}
								</div>
							)
						})}
					</div>
				)
			})}

			{order.giftCardRedemptions.map(redemption => (
				<div key={redemption.uuid}>
					<div
						className={clx(
							'grid grid-cols-1 items-center gap-x-4 px-3 py-2 text-ui-fg-subtle sm:grid-cols-2 sm:px-6 sm:py-4'
						)}
					>
						<div className='flex items-center gap-x-4'>
							<Thumbnail />
							<div>
								<Text size='small' leading='compact' weight='plus' className='text-ui-fg-base'>
									{redemption.name}
								</Text>
							</div>
						</div>

						<div className='items-center gap-x-4'>
							<div className='flex items-center justify-end gap-x-4'>
								<Text size='small'>
									{L.Amount.Default({
										amount: redemption.amount,
										currencyCode: order.currencyCode
									})}
								</Text>
							</div>
						</div>
					</div>
				</div>
			))}
		</div>
	)
}

const Cost = ({
	label,
	value,
	secondaryValue,
	tooltip,
	valuePrefix
}: {
	label: ReactNode
	value: string | number
	secondaryValue?: string
	tooltip?: ReactNode
	valuePrefix?: ReactNode
}) => (
	<div className='grid grid-cols-3 items-center'>
		<Text size='small' leading='compact'>
			{label} {tooltip}
		</Text>
		<div className='text-right'>
			<Text size='small' leading='compact'>
				{secondaryValue}
			</Text>
		</div>
		<div className='flex items-center justify-end gap-x-2'>
			{valuePrefix}
			<Text size='small' leading='compact'>
				{value}
			</Text>
		</div>
	</div>
)

const CostBreakdown = ({ order }: { order: Order }) => {
	const { L } = useI18n()
	const [isTaxOpen, setIsTaxOpen] = useState(false)

	const hasTaxLines = order.taxLines.length > 0
	const hasPointsEarned = order.pointsEarned !== undefined && order.pointsEarned > 0

	return (
		<div className='flex flex-col gap-y-2 px-3 py-2 text-ui-fg-subtle sm:px-6 sm:py-4'>
			<Cost
				label={L.OrderDetail.Summary.ItemTotal()}
				value={L.Amount.Default({
					amount: order.orderTotal,
					currencyCode: order.currencyCode
				})}
				valuePrefix={hasPointsEarned ? <PointsBadge>+{order.pointsEarned}</PointsBadge> : undefined}
			/>

			<div className='flex justify-between'>
				<div
					onClick={() => hasTaxLines && setIsTaxOpen(o => !o)}
					className={clx('flex items-center gap-1', {
						'cursor-pointer': hasTaxLines
					})}
				>
					<span className='txt-small select-none'>{L.OrderDetail.Summary.TaxTotal()}</span>
					{hasTaxLines && (
						<TriangleDownMini
							style={{
								transform: `rotate(${isTaxOpen ? 0 : -90}deg)`
							}}
						/>
					)}
				</div>

				<div className='text-right'>
					<Text size='small' leading='compact'>
						{L.Amount.Default({
							amount: order.taxTotal,
							currencyCode: order.currencyCode
						})}
					</Text>
				</div>
			</div>
			{isTaxOpen && (
				<div className='flex flex-col gap-1 pl-5'>
					{order.taxLines.map(({ code, total }) => {
						return (
							<div key={code} className='flex items-center justify-between gap-x-2'>
								<div>
									<span className='txt-small font-medium text-ui-fg-subtle'>{code}</span>
								</div>
								<div className='relative flex-1'>
									<div className='bottom-[calc(50% - 2px)] absolute h-[1px] w-full border-b border-dashed' />
								</div>
								<span className='txt-small text-ui-fg-muted'>
									{L.Amount.Default({
										amount: total,
										currencyCode: order.currencyCode
									})}
								</span>
							</div>
						)
					})}
				</div>
			)}
		</div>
	)
}

const Total = ({ order }: { order: Order }) => {
	const { L } = useI18n()

	// Refund amounts are stored as negative values, so this sum is <= 0.
	const refundedTotal = sum(
		(order.refunds ?? []).filter(refund => refund.status === 'SUCCESS').map(refund => refund.amount)
	)
	const hasRefund = smallerThan(refundedTotal, 0)
	// Outstanding is currentBalance (net - amountDue): negative after a refund (settled),
	// so only surface it when the customer genuinely owes/overpaid.
	const hasOutstanding = largerThan(order.outstandingAmount, 0)

	return (
		<div className='flex flex-col gap-y-2 px-3 py-2 sm:px-6 sm:py-4'>
			<div className='flex items-center justify-between text-ui-fg-base'>
				<Text weight='plus' className='text-ui-fg-subtle' size='small' leading='compact'>
					{L.OrderDetail.Summary.Total()}
				</Text>
				<Text weight='plus' className='text-ui-fg-subtle' size='small' leading='compact'>
					{L.Amount.Default({
						amount: order.orderTotal,
						currencyCode: order.currencyCode
					})}
				</Text>
			</div>

			{order.giftCardTotal.value !== 0 && (
				<div className='flex items-center justify-between text-ui-fg-base'>
					<Text weight='plus' className='text-ui-fg-subtle' size='small' leading='compact'>
						{L.OrderDetail.Summary.GiftCardTotal()}
					</Text>
					<Text weight='plus' className='text-ui-fg-subtle' size='small' leading='compact'>
						{L.Amount.Default({
							amount: order.giftCardTotal,
							currencyCode: order.currencyCode
						})}
					</Text>
				</div>
			)}

			<div className='flex items-center justify-between text-ui-fg-base'>
				<Text weight='plus' className='text-ui-fg-subtle' size='small' leading='compact'>
					{L.OrderDetail.Summary.PaidTotal()}
				</Text>
				<Text weight='plus' className='text-ui-fg-subtle' size='small' leading='compact'>
					{L.Amount.Default({
						amount: order.transactionNetAmount,
						currencyCode: order.currencyCode
					})}
				</Text>
			</div>

			{hasRefund && (
				<div className='flex items-center justify-between text-ui-fg-base'>
					<Text weight='plus' className='text-ui-fg-subtle' size='small' leading='compact'>
						{L.OrderDetail.Summary.Refunded()}
					</Text>
					<Text weight='plus' className='text-ui-fg-subtle' size='small' leading='compact'>
						{L.Amount.Default({
							amount: refundedTotal,
							currencyCode: order.currencyCode
						})}
					</Text>
				</div>
			)}

			{hasOutstanding && (
				<div className='flex items-center justify-between text-ui-fg-base'>
					<Text className='text-semibold text-ui-fg-subtle' size='small' leading='compact' weight='plus'>
						{L.OrderDetail.Summary.OutstandingAmount()}
					</Text>
					<Text className='text-bold text-ui-fg-subtle' size='small' leading='compact' weight='plus'>
						{L.Amount.Default({
							amount: order.outstandingAmount,
							currencyCode: order.currencyCode
						})}
					</Text>
				</div>
			)}
		</div>
	)
}
